import { InfoCircleOutlined } from '@ant-design/icons';
import { Collapse, Empty, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { BEHAVIOUR_LABELS } from '../../../common/constants';
import CommonTable from '../../../components/CommonTable';

const MONTH_COLUMNS = [
  {
    title: 'Behaviour',
    key: 'behaviourKey',
    width: 160,
    render: (_, record) =>
      BEHAVIOUR_LABELS[record.behaviourKey] || record.behaviourKey,
  },
  {
    title: 'Raw',
    dataIndex: 'rawCount',
    key: 'rawCount',
    align: 'center',
    width: 80,
  },
  {
    title: 'Genuine',
    dataIndex: 'excusedCount',
    key: 'excusedCount',
    align: 'center',
    width: 90,
  },
  {
    title: 'Effective',
    dataIndex: 'effectiveCount',
    key: 'effectiveCount',
    align: 'center',
    width: 90,
  },
];

// camelCase key on a monthlyBreakdown entry -> its snake_case behaviourKey
// (matches AttendanceTrackerView's BEHAVIOUR_COLUMNS mapping).
const BEHAVIOUR_ROW_DEFS = [
  { key: 'leave', behaviourKey: 'leave' },
  { key: 'wfhOver', behaviourKey: 'wfh_over' },
  { key: 'hoursOver', behaviourKey: 'hours_over' },
  { key: 'late', behaviourKey: 'late' },
  { key: 'early', behaviourKey: 'early' },
  { key: 'standup', behaviourKey: 'standup' },
  { key: 'xbrk', behaviourKey: 'xbrk' },
  { key: 'sbrk', behaviourKey: 'sbrk' },
];

/** All 8 behaviour rows for a month, sourced from its always-present
 * per-behaviour totals (not the sparse `incidents` array), so a behaviour
 * with zero counts still renders its own "0" row instead of being omitted. */
function buildBehaviourRows(monthEntry) {
  return BEHAVIOUR_ROW_DEFS.map(({ key, behaviourKey }) => {
    const counts = monthEntry?.[key] || {};
    return {
      id: `${monthEntry.year}-${monthEntry.month}-${behaviourKey}`,
      behaviourKey,
      rawCount: counts.rawCount ?? 0,
      excusedCount: counts.excusedCount ?? 0,
      effectiveCount: counts.effectiveCount ?? 0,
    };
  });
}

/** Colour bucket matching AttendanceTrackerView's ScoreBadge thresholds. */
function scoreTagColor(score) {
  if (score === null || score === undefined) return 'default';
  if (score >= 8.0) return 'green';
  if (score >= 6.0) return 'blue';
  if (score >= 4.0) return 'orange';
  return 'red';
}

/** Splits an array into consecutive chunks of at most `size` items. */
function chunk(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/** Average score across a group of months (null if none have a score). */
function averageScoreOf(group) {
  const scored = group
    .map((monthEntry) => monthEntry.score)
    .filter((score) => score !== null && score !== undefined);
  if (!scored.length) return null;
  return scored.reduce((sum, score) => sum + score, 0) / scored.length;
}

/** "Jun 2026" for a single month, "Jul 2026 - Sep 2026" for a group. */
function labelOf(group) {
  const newest = group[0];
  const oldest = group[group.length - 1];
  const format = (monthEntry) =>
    dayjs(`${monthEntry.year}-${monthEntry.month}-01`).format('MMM YYYY');
  return group.length > 1
    ? `${format(oldest)} - ${format(newest)}`
    : format(newest);
}

/**
 * UserAttendanceView renders one selected employee's monthly behaviour
 * totals (as already returned by the userAttendance query's
 * monthlyBreakdown), most-recent-first — including months with no incidents
 * at all, since every behaviour row is shown with its actual count (0 or
 * otherwise) rather than only rows that had a logged incident. Every rolling
 * group of up to 3 months (matching display order, e.g. Sep/Aug/Jul then
 * Jun) is preceded by a non-collapsible "Total" row averaging that group's
 * scores, followed by that group's individual month panels.
 */
function UserAttendanceView({ data, loading, onInfoClick }) {
  const months = useMemo(
    () =>
      (data || []).slice().sort((a, b) => b.year - a.year || b.month - a.month),
    [data],
  );

  const groups = useMemo(
    () =>
      chunk(months, 3).map((group) => ({
        key: `${group[0].year}-${group[0].month}`,
        label: labelOf(group),
        averageScore: averageScoreOf(group),
        months: group,
      })),
    [months],
  );

  if (!loading && months.length === 0) {
    return <Empty description="No attendance data for the selected range" />;
  }

  return (
    <div className="user-attendance-months">
      {groups.map((group) => (
        <React.Fragment key={group.key}>
          <div className="user-attendance-total">
            <div className="user-attendance-month-header">
              <span className="user-attendance-month-title">
                Total ({group.label})
              </span>
              {group.averageScore !== null && (
                <Tag color={scoreTagColor(group.averageScore)}>
                  Score {group.averageScore.toFixed(1)}
                </Tag>
              )}
            </div>
          </div>
          {group.months.map((monthEntry) => (
            <Collapse
              key={`${monthEntry.year}-${monthEntry.month}`}
              className="user-attendance-month-collapse"
              bordered={false}
              defaultActiveKey={[`${monthEntry.year}-${monthEntry.month}`]}
              items={[
                {
                  key: `${monthEntry.year}-${monthEntry.month}`,
                  label: (
                    <div className="user-attendance-month-header">
                      <span className="user-attendance-month-title">
                        {dayjs(
                          `${monthEntry.year}-${monthEntry.month}-01`,
                        ).format('MMMM YYYY')}
                      </span>
                      {monthEntry.score !== null &&
                        monthEntry.score !== undefined && (
                          <>
                            <Tag color={scoreTagColor(monthEntry.score)}>
                              Score {monthEntry.score.toFixed(1)}
                            </Tag>
                            <InfoCircleOutlined
                              className="user-attendance-score-info"
                              onClick={(e) => {
                                e.stopPropagation();
                                onInfoClick?.(monthEntry);
                              }}
                            />
                          </>
                        )}
                    </div>
                  ),
                  children: (
                    <CommonTable
                      size="small"
                      bordered
                      columns={MONTH_COLUMNS}
                      data={buildBehaviourRows(monthEntry)}
                      loadingData={loading}
                      rowKey={(obj) => obj?.id}
                    />
                  ),
                },
              ]}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}

export default UserAttendanceView;
